import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HarmonyXClose } from "@/components/icons/harmony-icons-inline";
import { usePricingActions } from "@/features/pricing/hooks/use-pricing-actions";
import { useBillingHistory } from "@/api/hooks/use-query-api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, AlertTriangle, Zap, Crown } from "lucide-react";

import { useLeftSidebar } from "@/components/ui/sidebar";

interface CreditCardSectionProps {
  className?: string;
}

export default function UserCreditsSection({
  className = "",
}: CreditCardSectionProps) {
  const { t } = useTranslation();
  const { open: isSidebarOpen } = useLeftSidebar();
  const [creditCardDismissed, setCreditCardDismissed] = useState(false);
  const { openPricingModalWithTopup } = usePricingActions();

  // Billing history hook - get credit data from billing endpoint
  const { data: billingData, isLoading: isBillingLoading } =
    useBillingHistory();

  // Calculate percentage from billing data (quota logic: remaining credits)
  const calculatePercentage = () => {
    if (!billingData?.data.usage) return 0;

    const { subscription: remainingSubscription, oneTime: remainingOneTime } =
      billingData.data.usage.tokens;
    const { subscription: maxTokens, oneTime: maxOneTime } =
      billingData.data.usage.maxTokens;

    const totalRemaining = remainingSubscription + remainingOneTime;
    const totalMax = maxTokens + maxOneTime;

    if (totalMax === 0) return 0;

    // Quota logic: show remaining credits percentage (100% = full quota, 0% = empty quota)
    const calculatedPercentage = Math.round((totalRemaining / totalMax) * 100);
    return Math.min(calculatedPercentage, 100);
  };

  const percentage = calculatePercentage();
  const totalRemaining = billingData?.data.usage
    ? billingData.data.usage.tokens.subscription +
      billingData.data.usage.tokens.oneTime
    : 0;
  const totalMax = billingData?.data.usage
    ? billingData.data.usage.maxTokens.subscription +
      billingData.data.usage.maxTokens.oneTime
    : 0;
  const totalUsed = totalMax - totalRemaining;
  const remaining = totalRemaining;

  // Determine status and styling (quota logic: high percentage = good, low percentage = critical)
  const getStatusInfo = () => {
    if (percentage <= 10) {
      return {
        status: "critical" as const,
        icon: AlertTriangle,
        color: "text-red-500 dark:text-red-400",
        bgColor: "bg-red-50 dark:bg-red-950/20",
        borderColor: "border-red-200 dark:border-red-800",
        badgeColor:
          "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300",
        message: t("credits.criticalUsage"),
      };
    } else if (percentage <= 30) {
      return {
        status: "warning" as const,
        icon: TrendingUp,
        color: "text-orange-500 dark:text-orange-400",
        bgColor: "bg-orange-50 dark:bg-orange-950/20",
        borderColor: "border-orange-200 dark:border-orange-800",
        badgeColor:
          "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
        message: t("credits.highUsage"),
      };
    } else {
      return {
        status: "good" as const,
        icon: Zap,
        color: "text-green-500 dark:text-green-400",
        bgColor: "bg-green-50 dark:bg-green-950/20",
        borderColor: "border-green-200 dark:border-green-800",
        badgeColor:
          "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300",
        message: t("credits.goodUsage"),
      };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  if (creditCardDismissed || percentage > 20) {
    return null;
  }

  // Loading state - show skeleton
  if (isBillingLoading) {
    if (!isSidebarOpen) {
      return (
        <div className={`shrink-0 flex justify-center py-2 ${className}`}>
          <div className="flex flex-col items-center gap-1.5 px-2 py-1.5 rounded-md bg-bg-secondary dark:bg-gray-800/50 w-12">
            <div className="w-10 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
            <div className="w-4 h-2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="w-3 h-3 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
          </div>
        </div>
      );
    }

    return (
      <div className={`shrink-0 px-4 py-3 ${className}`}>
        <Card className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
          <CardContent className="p-4">
            <div className="animate-pulse space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  <div className="space-y-1">
                    <div className="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="w-24 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
                <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
              <div className="space-y-2">
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div className="w-8 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="w-12 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                  <div className="space-y-1">
                    <div className="w-12 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="w-16 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Sidebar kapalı iken minimal progress bar göster
  if (!isSidebarOpen) {
    return (
      <div className={`shrink-0 flex justify-center py-2 ${className}`}>
        <div
          className="relative cursor-pointer group"
          onClick={() => openPricingModalWithTopup()}
        >
          <div className="flex flex-col items-center gap-1.5 px-2 py-1.5 rounded-md bg-bg-secondary dark:bg-gray-800/50 hover:bg-bg-secondary/80 dark:hover:bg-gray-800/70 transition-colors w-12">
            {/* Progress Bar */}
            <div className="w-10 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  percentage <= 10
                    ? "bg-red-500"
                    : percentage <= 30
                      ? "bg-orange-500"
                      : "bg-green-500"
                }`}
                style={{ width: `${Math.max(percentage, 2)}%` }} // Minimum 2% width for visibility
              />
            </div>

            {/* Percentage */}
            <span className="text-[10px] font-semibold text-text-primary dark:text-white leading-none">
              {percentage}%
            </span>

            {/* Status Icon */}
            <div className={`p-0.5 rounded-full ${statusInfo.bgColor}`}>
              <StatusIcon className={`w-2.5 h-2.5 ${statusInfo.color}`} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`shrink-0 ${className}`}>
      {/* Mobile View - Compact Horizontal Banner */}
      <div className="md:hidden">
        <div
          className={`mx-3 my-1.5 rounded-xl ${statusInfo.bgColor} ${statusInfo.borderColor} border p-2.5 transition-all duration-200`}
        >
          {/* Mobile Header Row */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className={`p-1 rounded-lg shrink-0 ${statusInfo.bgColor}`}>
                <StatusIcon className={`w-3.5 h-3.5 ${statusInfo.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-xs text-text-primary dark:text-white truncate">
                  {billingData?.data.pricingPlan?.name || t("credits.title")}
                </h3>
                <p className="text-[10px] text-text-tertiary-600 dark:text-gray-400 truncate">
                  {statusInfo.message}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCreditCardDismissed(true)}
              className="h-6 w-6 p-0 shrink-0 hover:bg-black/5 dark:hover:bg-white/5 flex items-center justify-center"
            >
              <HarmonyXClose
                size={10}
                className="text-text-tertiary-600 dark:text-gray-400"
              />
            </Button>
          </div>

          {/* Mobile Progress Bar Row */}
          <div className="space-y-1.5 mb-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-medium text-text-primary dark:text-white">
                {t("credits.usage")}
              </span>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${statusInfo.badgeColor}`}
              >
                {percentage}%
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200/50 dark:bg-gray-700/50 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  percentage <= 10
                    ? "bg-red-500"
                    : percentage <= 30
                      ? "bg-orange-500"
                      : "bg-green-500"
                }`}
                style={{ width: `${Math.max(percentage, 2)}%` }}
              />
            </div>
            {/* Mobile Credit Stats - Horizontal */}
            <div className="flex items-center justify-between text-[10px]">
              <div className="flex items-center gap-1">
                <span className="text-text-tertiary-600 dark:text-gray-400">
                  {t("credits.used")}:
                </span>
                <span className="font-semibold text-text-primary dark:text-white">
                  {totalUsed.toLocaleString()}
                </span>
              </div>
              <div className="w-px h-2.5 bg-border-secondary dark:bg-gray-600" />
              <div className="flex items-center gap-1">
                <span className="text-text-tertiary-600 dark:text-gray-400">
                  {t("credits.remaining")}:
                </span>
                <span className="font-semibold text-text-primary dark:text-white">
                  {remaining.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Mobile Action Button - Single Full Width */}
          <Button
            size="sm"
            variant="darkPrimary"
            onClick={() => openPricingModalWithTopup()}
            className={`w-full h-9 text-xs font-semibold rounded-lg shadow-sm active:scale-[0.98] transition-transform
              ${
                percentage <= 10
                  ? "bg-red-600 hover:bg-red-500 dark:bg-red-600 dark:hover:bg-red-500 text-white"
                  : percentage <= 30
                    ? "bg-orange-600 hover:bg-orange-500 dark:bg-orange-600 dark:hover:bg-orange-500 text-white"
                    : "bg-green-600 hover:bg-green-500 dark:bg-green-600 dark:hover:bg-green-500 text-white"
              }`}
          >
            <Crown className="w-3.5 h-3.5 mr-1" />
            {t("credits.upgrade")}
          </Button>
        </div>
      </div>

      {/* Desktop View - Compact Pill */}
      <div className="hidden md:block px-2 py-1.5">
        <div
          className={`group relative rounded-xl border ${statusInfo.borderColor} ${statusInfo.bgColor} px-3 py-2.5 transition-all duration-200 hover:shadow-sm`}
        >
          {/* Dismiss (hover'da görünür) */}
          <button
            onClick={() => setCreditCardDismissed(true)}
            className="absolute top-1.5 right-1.5 h-5 w-5 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 transition-opacity focus:outline-none focus:opacity-100"
            title={t("credits.dismiss")}
          >
            <HarmonyXClose
              size={10}
              className="text-text-tertiary-600 dark:text-gray-400"
            />
          </button>

          <button
            onClick={() => openPricingModalWithTopup()}
            className="w-full text-left focus:outline-none"
          >
            {/* Üst satır: durum + kalan kredi + yüzde */}
            <div className="flex items-center gap-1.5 mb-1.5 pr-4">
              <StatusIcon className={`w-3.5 h-3.5 shrink-0 ${statusInfo.color}`} />
              <span className="text-xs font-semibold text-text-primary dark:text-white truncate flex-1">
                {t("credits.remaining")}: {remaining.toLocaleString()}
              </span>
              <span
                className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${statusInfo.badgeColor}`}
              >
                {percentage}%
              </span>
            </div>

            {/* İnce progress bar */}
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-2">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  percentage <= 10
                    ? "bg-red-500"
                    : percentage <= 30
                      ? "bg-orange-500"
                      : "bg-green-500"
                }`}
                style={{ width: `${Math.max(percentage, 2)}%` }}
              />
            </div>

            {/* Upgrade CTA - tek satır */}
            <div
              className={`flex items-center justify-center gap-1.5 h-7 rounded-lg text-[11px] font-semibold text-white transition-colors ${
                percentage <= 10
                  ? "bg-red-600 group-hover:bg-red-500"
                  : percentage <= 30
                    ? "bg-orange-600 group-hover:bg-orange-500"
                    : "bg-green-600 group-hover:bg-green-500"
              }`}
            >
              <Crown className="w-3 h-3" />
              {t("credits.upgrade")}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
